import React from 'react';
import { Flex, Grid, Progress, Spinner, Text } from '@/components/ui';
import { IconCircleCheckFilled, IconCircleXFilled } from '@tabler/icons-react';

interface ImportProgressSectionProps {
  status: string;
  recordsProcessed?: number;
  totalRecords?: number;
  progressPercentage?: number;
  successCount?: number;
  errorCount?: number;
  errorMessage?: string;
  showSpinner?: boolean;
}

export const ImportProgressSection: React.FC<ImportProgressSectionProps> = ({
  status,
  recordsProcessed,
  totalRecords,
  progressPercentage,
  successCount,
  errorCount,
  errorMessage,
  showSpinner = false,
}) => {
  if (
    status !== 'processing' &&
    status !== 'completed' &&
    status !== 'failed' &&
    status !== 'valid_for_import' &&
    status !== 'invalid_for_import' &&
    status !== 'imported'
  ) {
    return null;
  }

  // Determine progress value and color
  let progressValue = progressPercentage ?? 10;
  let progressColor: 'success' | 'error' | 'info' = 'info';

  if (status === 'imported') {
    progressValue = 100;
    progressColor = 'success';
  } else if (status === 'failed') {
    progressColor = 'error';
  } else if (status === 'completed') {
    progressColor = 'success';
  } else if (status === 'invalid_for_import') {
    // Show small progress (1-2%) to simulate attempted import that failed
    progressValue = progressPercentage !== undefined ? progressPercentage : 2;
    progressColor = 'error';
  } else if (status === 'valid_for_import') {
    // Show some progress indication, but not 100%
    // Use progressPercentage if available, otherwise show 50% as default
    progressValue = progressPercentage !== undefined ? progressPercentage : 50;
    progressColor = 'info';
  }

  return (
    <div>
      <Flex align="center" justify="between" mb="2">
        {recordsProcessed !== undefined && totalRecords !== undefined ? (
          <Text size="3xsmall" color="gray">
            {recordsProcessed || 0} / {totalRecords || 0} records
          </Text>
        ) : null}
      </Flex>
      <Grid align="center" gap="2" style={{ gridTemplateColumns: '1fr auto' }}>
        <Progress
          value={progressValue}
          max={100}
          color={progressColor}
          size="small"
          style={{ flex: 1 }}
          highContrast={
            progressColor !== 'error' && progressColor !== 'success'
          }
        />
        {showSpinner && <Spinner size="1" />}
        {progressColor === 'success' && (
          <IconCircleCheckFilled color="var(--success-main)" />
        )}
        {progressColor === 'error' && (
          <IconCircleXFilled color="var(--error-main)" />
        )}
      </Grid>
      {status === 'completed' && successCount !== undefined && (
        <Flex gap="4" mt="2">
          <Text size="3xsmall" color="success">
            ✓ {successCount} successful
          </Text>
          {errorCount && errorCount > 0 && (
            <Text size="3xsmall" color="error">
              ✗ {errorCount} errors
            </Text>
          )}
        </Flex>
      )}
      {status === 'imported' && (
        <Text size="2" color="success" align="center" mt="2">
          Import completed successfully
        </Text>
      )}
      {status === 'valid_for_import' && (
        <Text size="2" color="gray" align="center" mt="2">
          <Spinner
            style={{ display: 'inline-block', verticalAlign: 'middle' }}
            mr="2"
            size="1"
          />
          Import in progress...
        </Text>
      )}
      {status === 'invalid_for_import' && (
        <Text size="2" color="error" align="center" mt="2">
          Validation failed. Import cannot proceed due to validation warnings.
        </Text>
      )}
      {status === 'failed' && errorMessage && (
        <Text size="3xsmall" color="error" mt="2">
          Error: {errorMessage}
        </Text>
      )}
    </div>
  );
};
